import { z } from "zod";
export const PLANNER_PENDING_SOURCE_PROMOTION_MAX_AGE_MS = 23 * 60 * 60 * 1_000;
export const PLANNER_LEGACY_RAW_RESULT_SUFFIX = ".planner-races-v2.json";
const stringListSchema = z
    .union([z.string(), z.array(z.string())])
    .transform((value) => (typeof value === "string" ? [value] : value));
const policyStatementSchema = z
    .object({
    Sid: z.string().optional(),
    Effect: z.string(),
    Action: stringListSchema,
    Principal: z.unknown(),
    Resource: stringListSchema,
    Condition: z.unknown().optional(),
})
    .passthrough();
const policySchema = z
    .object({ Statement: z.array(policyStatementSchema) })
    .passthrough();
const lifecycleRuleSchema = z
    .object({
    ID: z.string().optional(),
    Status: z.literal("Enabled"),
    Prefix: z.string().optional(),
    Filter: z.unknown().optional(),
    Expiration: z
        .union([
        z.object({ Days: z.number().int().positive() }).strict(),
        z.object({ ExpiredObjectDeleteMarker: z.literal(true) }).strict(),
    ])
        .optional(),
    NoncurrentVersionExpiration: z
        .object({ NoncurrentDays: z.number().int().positive() })
        .strict()
        .optional(),
    Transitions: z.unknown().optional(),
    NoncurrentVersionTransitions: z.unknown().optional(),
})
    .passthrough();
const approvedPlannerExpirationRules = [
    {
        id: "ExpirePlannerStaging",
        prefix: "private/condition-books/staging/",
        currentDays: 1,
    },
    {
        id: "ExpirePlannerRawResults",
        prefix: "private/condition-books/raw-results/",
        currentDays: 30,
    },
    {
        id: "ExpirePlannerPendingRegistrations",
        prefix: "private/condition-books/registered/",
        currentDays: 1,
        tag: { key: "hisani-registration-state", value: "pending" },
    },
    {
        id: "ExpirePlannerLegacyOrphans",
        prefix: "private/condition-books/",
        currentDays: 1,
        tag: { key: "hisani-retention-class", value: "legacy-orphan" },
    },
    {
        id: "ExpirePlannerLegacyRawEvidence",
        prefix: "private/condition-books/",
        currentDays: 30,
        tag: { key: "hisani-retention-class", value: "legacy-raw" },
    },
];
const plannerSourceName = "[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}-[A-Za-z0-9._-]+";
const plannerRegistrationId = "[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}";
const plannerStagingObjectKey = new RegExp(`^private/condition-books/staging/${plannerSourceName}$`, "i");
const legacyPlannerSourceObjectKey = new RegExp(`^private/condition-books/${plannerSourceName}$`, "i");
const plannerRegisteredSourceObjectKey = new RegExp(`^private/condition-books/registered/${plannerRegistrationId}-source$`, "i");
export function isPlannerRegistrationSourceObjectKey(value) {
    return (plannerStagingObjectKey.test(value) ||
        legacyPlannerSourceObjectKey.test(value));
}
export function isLegacyPlannerSourceObjectKey(value) {
    return legacyPlannerSourceObjectKey.test(value);
}
export function isLegacyPlannerRawObjectKey(value) {
    return (value.endsWith(PLANNER_LEGACY_RAW_RESULT_SUFFIX) &&
        isLegacyPlannerSourceObjectKey(value.slice(0, -PLANNER_LEGACY_RAW_RESULT_SUFFIX.length)));
}
export function isPlannerRegisteredSourceObjectKey(value) {
    return plannerRegisteredSourceObjectKey.test(value);
}
export function isPlannerStoragePolicyReady(document, bucketName, cleanupPrincipalArn) {
    let value;
    try {
        value = JSON.parse(document);
    }
    catch {
        return false;
    }
    const parsed = policySchema.safeParse(value);
    if (!parsed.success)
        return false;
    const immutableWrites = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerImmutableWrites");
    const retainedSources = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerRegisteredSourceRetention");
    const committedRegistrationTags = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerCommittedRegistrationTagsOnly");
    const initialRegistrationTags = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerInitialRegistrationTagsOnly");
    const pendingRegistrationTags = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerPendingRegistrationTagsOnly");
    const requiredRegistrationTag = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerRegisteredTagKeyRequired");
    const exclusiveRegistrationTag = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerRegisteredTagKeysOnly");
    const provisionalRegistrationTags = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerProvisionalRegistrationTagRetention");
    const retainedRegistrationTags = parsed.data.Statement.find(({ Sid }) => Sid === "HisaniPlannerCommittedRegistrationTagRetention");
    return (immutableWrites?.Effect === "Deny" &&
        isAnyPrincipal(immutableWrites.Principal) &&
        immutableWrites.Action.includes("s3:PutObject") &&
        immutableWrites.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/registered/*")) &&
        immutableWrites.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/raw-results/*")) &&
        hasConditionalWriteGuard(immutableWrites.Condition) &&
        retainedSources?.Effect === "Deny" &&
        isAnyPrincipal(retainedSources.Principal) &&
        retainedSources.Action.includes("s3:DeleteObject") &&
        retainedSources.Action.includes("s3:DeleteObjectVersion") &&
        retainedSources.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/registered/*")) &&
        retainsRegisteredSourcesExceptCleanupPrincipal(retainedSources.Condition, cleanupPrincipalArn) &&
        isRegisteredTagBoundaryStatement(requiredRegistrationTag, bucketName) &&
        requiresRegistrationStateTag(requiredRegistrationTag.Condition) &&
        isRegisteredTagBoundaryStatement(exclusiveRegistrationTag, bucketName) &&
        deniesUnexpectedRegistrationTagKeys(exclusiveRegistrationTag.Condition) &&
        initialRegistrationTags?.Effect === "Deny" &&
        isAnyPrincipal(initialRegistrationTags.Principal) &&
        initialRegistrationTags.Action.includes("s3:PutObject") &&
        initialRegistrationTags.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/registered/*")) &&
        requiresInitialPendingRegistrationTag(initialRegistrationTags.Condition) &&
        pendingRegistrationTags?.Effect === "Deny" &&
        isAnyPrincipal(pendingRegistrationTags.Principal) &&
        pendingRegistrationTags.Action.includes("s3:PutObjectTagging") &&
        pendingRegistrationTags.Action.includes("s3:PutObjectVersionTagging") &&
        pendingRegistrationTags.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/registered/*")) &&
        deniesRegistrationTagTransition(pendingRegistrationTags.Condition, "pending", ["provisional"]) &&
        committedRegistrationTags?.Effect === "Deny" &&
        isAnyPrincipal(committedRegistrationTags.Principal) &&
        committedRegistrationTags.Action.includes("s3:PutObjectTagging") &&
        committedRegistrationTags.Action.includes("s3:PutObjectVersionTagging") &&
        committedRegistrationTags.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/registered/*")) &&
        deniesRegistrationTagTransition(committedRegistrationTags.Condition, "committed", ["committed"]) &&
        provisionalRegistrationTags?.Effect === "Deny" &&
        isAnyPrincipal(provisionalRegistrationTags.Principal) &&
        provisionalRegistrationTags.Action.includes("s3:PutObjectTagging") &&
        provisionalRegistrationTags.Action.includes("s3:PutObjectVersionTagging") &&
        provisionalRegistrationTags.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/registered/*")) &&
        deniesRegistrationTagTransition(provisionalRegistrationTags.Condition, "provisional", ["provisional", "committed"]) &&
        retainedRegistrationTags?.Effect === "Deny" &&
        isAnyPrincipal(retainedRegistrationTags.Principal) &&
        retainedRegistrationTags.Action.includes("s3:DeleteObjectTagging") &&
        retainedRegistrationTags.Action.includes("s3:DeleteObjectVersionTagging") &&
        retainedRegistrationTags.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/registered/*")) &&
        retainedRegistrationTags.Condition === undefined);
}
function isRegisteredTagBoundaryStatement(statement, bucketName) {
    return (statement?.Effect === "Deny" &&
        isAnyPrincipal(statement.Principal) &&
        statement.Action.includes("s3:PutObject") &&
        statement.Action.includes("s3:PutObjectTagging") &&
        statement.Action.includes("s3:PutObjectVersionTagging") &&
        statement.Resource.some((resource) => isBucketObjectResource(resource, bucketName, "private/condition-books/registered/*")));
}
function requiresRegistrationStateTag(value) {
    return z
        .object({
        Null: z
            .object({
            "s3:RequestObjectTag/hisani-registration-state": z.literal("true"),
        })
            .strict(),
    })
        .strict()
        .safeParse(value).success;
}
function deniesUnexpectedRegistrationTagKeys(value) {
    const parsed = z
        .object({
        "ForAnyValue:StringNotEquals": z
            .object({ "s3:RequestObjectTagKeys": stringListSchema })
            .strict(),
    })
        .strict()
        .safeParse(value);
    return (parsed.success &&
        sameStringSet(parsed.data["ForAnyValue:StringNotEquals"]["s3:RequestObjectTagKeys"], ["hisani-registration-state"]));
}
function requiresInitialPendingRegistrationTag(value) {
    return z
        .object({
        StringNotEquals: z
            .object({
            "s3:RequestObjectTag/hisani-registration-state": z.literal("pending"),
        })
            .strict(),
    })
        .strict()
        .safeParse(value).success;
}
function isAnyPrincipal(value) {
    if (value === "*")
        return true;
    const parsed = z
        .object({ AWS: z.literal("*") })
        .passthrough()
        .safeParse(value);
    return parsed.success;
}
function isBucketObjectResource(resource, bucketName, key) {
    return resource.endsWith(`:s3:::${bucketName}/${key}`);
}
export function isPlannerStorageLifecycleReady(value) {
    const parsed = z
        .object({ Rules: z.array(lifecycleRuleSchema) })
        .safeParse(value);
    if (!parsed.success)
        return false;
    return (!parsed.data.Rules.some(isUnapprovedPlannerLifecycleAction) &&
        approvedPlannerExpirationRules.every((expected) => hasLifecycleRule(parsed.data.Rules, expected)) &&
        hasExpiredDeleteMarkerRule(parsed.data.Rules));
}
function isUnapprovedPlannerLifecycleAction(rule) {
    if (rule.Expiration === undefined &&
        rule.NoncurrentVersionExpiration === undefined &&
        rule.Transitions === undefined &&
        rule.NoncurrentVersionTransitions === undefined) {
        return false;
    }
    const filter = lifecycleFilter(rule);
    const plannerPrefix = "private/condition-books/";
    const prefix = filter.prefix ?? "";
    if (!plannerPrefix.startsWith(prefix) && !prefix.startsWith(plannerPrefix)) {
        return false;
    }
    return (!approvedPlannerExpirationRules.some((expected) => matchesLifecycleRule(rule, expected)) && !matchesExpiredDeleteMarkerRule(rule));
}
function hasExpiredDeleteMarkerRule(rules) {
    return rules.some(matchesExpiredDeleteMarkerRule);
}
function matchesExpiredDeleteMarkerRule(rule) {
    const filter = lifecycleFilter(rule);
    return (rule.ID === "RemovePlannerExpiredDeleteMarkers" &&
        rule.Expiration !== undefined &&
        "ExpiredObjectDeleteMarker" in rule.Expiration &&
        rule.Expiration.ExpiredObjectDeleteMarker &&
        rule.NoncurrentVersionExpiration === undefined &&
        rule.Transitions === undefined &&
        rule.NoncurrentVersionTransitions === undefined &&
        filter.prefix === "private/condition-books/" &&
        filter.tags.length === 0);
}
function retainsRegisteredSourcesExceptCleanupPrincipal(value, cleanupPrincipalArn) {
    const parsed = z
        .object({
        ArnNotEquals: z.object({ "aws:PrincipalArn": z.string() }).strict(),
    })
        .strict()
        .safeParse(value);
    return (parsed.success &&
        cleanupPrincipalArn.length > 0 &&
        !cleanupPrincipalArn.includes("*") &&
        parsed.data.ArnNotEquals["aws:PrincipalArn"] === cleanupPrincipalArn);
}
function hasConditionalWriteGuard(value) {
    const parsed = z
        .object({
        Null: z.object({ "s3:if-none-match": z.literal("true") }).strict(),
    })
        .strict()
        .safeParse(value);
    return parsed.success;
}
function deniesRegistrationTagTransition(value, existingState, allowedRequestedStates) {
    const parsed = z
        .object({
        StringEquals: z
            .object({
            "s3:ExistingObjectTag/hisani-registration-state": z.string(),
        })
            .strict(),
        StringNotEquals: z
            .object({
            "s3:RequestObjectTag/hisani-registration-state": stringListSchema,
        })
            .strict(),
    })
        .strict()
        .safeParse(value);
    return (parsed.success &&
        parsed.data.StringEquals["s3:ExistingObjectTag/hisani-registration-state"] === existingState &&
        sameStringSet(parsed.data.StringNotEquals["s3:RequestObjectTag/hisani-registration-state"], allowedRequestedStates));
}
function sameStringSet(actual, expected) {
    return (actual.length === expected.length &&
        expected.every((value) => actual.includes(value)));
}
function hasLifecycleRule(rules, expected) {
    const rule = rules.find(({ ID }) => ID === expected.id);
    return rule !== undefined && matchesLifecycleRule(rule, expected);
}
function matchesLifecycleRule(rule, expected) {
    if (rule.ID !== expected.id ||
        rule.Expiration === undefined ||
        !("Days" in rule.Expiration) ||
        rule.Expiration.Days !== expected.currentDays ||
        rule.NoncurrentVersionExpiration?.NoncurrentDays !== 1 ||
        rule.Transitions !== undefined ||
        rule.NoncurrentVersionTransitions !== undefined) {
        return false;
    }
    const filter = lifecycleFilter(rule);
    return (filter.prefix === expected.prefix &&
        (expected.tag === undefined
            ? filter.tags.length === 0
            : filter.tags.length === 1 &&
                filter.tags[0]?.Key === expected.tag.key &&
                filter.tags[0].Value === expected.tag.value));
}
function lifecycleFilter(rule) {
    const filter = z
        .object({
        Prefix: z.string().optional(),
        Tag: z.object({ Key: z.string(), Value: z.string() }).strict().optional(),
        And: z
            .object({
            Prefix: z.string().optional(),
            Tags: z
                .array(z.object({ Key: z.string(), Value: z.string() }).strict())
                .optional(),
        })
            .strict()
            .optional(),
    })
        .strict()
        .safeParse(rule.Filter);
    if (!filter.success)
        return { prefix: rule.Prefix, tags: [] };
    return {
        prefix: filter.data.And?.Prefix ?? filter.data.Prefix ?? rule.Prefix,
        tags: [
            ...(filter.data.Tag === undefined ? [] : [filter.data.Tag]),
            ...(filter.data.And?.Tags ?? []),
        ],
    };
}
